/**
 * One `Dialog` with two steps: the past seasons list, newest first, and one
 * season's summary (ISSUE-754, S8). No URL access and no data fetching here;
 * `PastSeasonsDialogContainer` owns both.
 */
import { Button, Dialog } from "@sportsmet/ui";
import type { JSX } from "react";

import {
  PAST_SEASONS_BACK_LABEL,
  PAST_SEASONS_DIALOG_TITLE,
  type typePastSeasonRow,
  type typePastSeasonSummaryPhase,
  type typeSeasonLeaderboardMember,
} from "./pastSeasonsModel";
import { PastSeasonSummary } from "./PastSeasonSummary";

export type typePastSeasonsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: "list" | "summary";
  rows: readonly typePastSeasonRow[];
  onSelectSeason: (seasonId: string) => void;
  summaryTitle: string;
  summaryDescription: string | undefined;
  summaryPhase: typePastSeasonSummaryPhase;
  summaryMembers: readonly typeSeasonLeaderboardMember[];
  onBackToList: () => void;
  onRetry: () => void;
};

export const PastSeasonsDialog = ({
  open,
  onOpenChange,
  step,
  rows,
  onSelectSeason,
  summaryTitle,
  summaryDescription,
  summaryPhase,
  summaryMembers,
  onBackToList,
  onRetry,
}: typePastSeasonsDialogProps): JSX.Element => {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={step === "list" ? PAST_SEASONS_DIALOG_TITLE : summaryTitle}
      description={step === "summary" ? summaryDescription : undefined}
      autoFocus={false}
    >
      {step === "list" ? (
        <ul className="flex flex-col gap-2" data-testid="past-seasons-list">
          {rows.map((row) => (
            <li key={row.seasonId}>
              <Button
                variant="secondary"
                fullWidth
                multiline
                data-testid="past-season-open"
                onClick={() => {
                  onSelectSeason(row.seasonId);
                }}
              >
                <span className="flex flex-col">
                  <span className="text-body text-ink">{row.title}</span>
                  <span className="text-caption text-ink-secondary">
                    {row.meta}
                  </span>
                </span>
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col gap-3">
          <Button
            variant="ghost"
            size="sm"
            data-testid="past-seasons-back"
            onClick={onBackToList}
          >
            {PAST_SEASONS_BACK_LABEL}
          </Button>
          <PastSeasonSummary
            phase={summaryPhase}
            members={summaryMembers}
            onRetry={onRetry}
          />
        </div>
      )}
    </Dialog>
  );
};
